import React, { FC, forwardRef } from 'react';
import { PickersActionBarProps } from '@mui/x-date-pickers';
import { Box, Button } from '@mui/material';
import { usePickerContext, usePickerTranslations } from '@mui/x-date-pickers/hooks';

/**
 * Customization of action buttons in Date/Time pickers
 * Taken from original code:
 *  https://github.com/mui/mui-x/blob/master/packages/x-date-pickers/src/PickersActionBar/PickersActionBar.tsx
 */
export const FlowPickersActionsBar: FC<PickersActionBarProps> = forwardRef(({ actions, ...rest }, ref) => {
  const translations = usePickerTranslations();
  const {
    view,
    variant,
    clearValue,
    setValueToToday,
    acceptValueChanges,
    cancelValueChanges,
    goToNextStep,
    hasNextStep,
  } = usePickerContext();

  return (
    <Box
      sx={{
        ...(!!actions?.length && {
          p: 2,
          ...(['hours', 'minutes', 'seconds', 'meridiem'].includes(view) &&
            variant === 'desktop' && {
              p: 1,
            }),
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'flex-end',
        }),
      }}
      ref={ref}
      {...rest}
    >
      {actions.includes('cancel') && (
        <Button size="small" variant="text" color="secondary" onClick={cancelValueChanges}>
          {translations.cancelButtonLabel}
        </Button>
      )}
      {actions.includes('clear') && (
        <Button size="small" color="surface" onClick={clearValue}>
          {translations.clearButtonLabel}
        </Button>
      )}
      {actions.includes('today') && (
        <Button size="small" color="surface" onClick={setValueToToday}>
          {translations.todayButtonLabel}
        </Button>
      )}
      {actions.includes('next') && (
        <Button size="small" color="surface" onClick={goToNextStep}>
          {translations.nextStepButtonLabel}
        </Button>
      )}
      {actions.includes('nextOrAccept') && (
        <Button size="small" color="surface" onClick={hasNextStep ? goToNextStep : acceptValueChanges}>
          {hasNextStep ? translations.nextStepButtonLabel : translations.okButtonLabel}
        </Button>
      )}
      {actions.includes('accept') && (
        <Button size="small" color="surface" onClick={acceptValueChanges}>
          {translations.okButtonLabel}
        </Button>
      )}
    </Box>
  );
});
